/**
 * Pure counts and per-row action flags for the owner-invoice preview.
 * Reads existing preview fields only — does not recalculate the budget.
 */

export function unitRowActions(unit) {
  const hasInvoice = Boolean(unit?.invoice_id);
  const isDraft = unit?.invoice_status === "draft";
  const isPosted = unit?.invoice_status === "posted";
  const hasPdf = Boolean(unit?.pdf_document_id);
  const totalsMatch = unit?.invoice_total_matches_generated !== false;

  return {
    canPost: isDraft && hasPdf && totalsMatch,
    canGeneratePdf: hasInvoice && (isDraft || isPosted),
    showRegeneratePdf: hasInvoice && hasPdf && (isDraft || isPosted),
    canViewDownload: hasInvoice && Boolean(unit?.pdf_download_url),
    needsPdf: hasInvoice && !hasPdf,
    needsRegenerate: isDraft && hasPdf && !totalsMatch,
    statusLabel: isPosted
      ? "Posted to accounts"
      : isDraft
        ? "Draft"
        : hasInvoice
          ? unit.invoice_status
          : "Preview",
  };
}

export function invoiceLifecycle(preview) {
  const units = preview?.units || [];
  const rows = units.map((unit) => ({
    unit,
    actions: unitRowActions(unit),
  }));

  const draftsToCreate = preview?.new_unit_count ?? units.filter((u) => !u.invoice_id).length;
  const draftUnits = units.filter((u) => u.invoice_status === "draft");
  const postedUnits = units.filter((u) => u.invoice_status === "posted");
  const readyToPost = rows.filter((row) => row.actions.canPost).map((row) => row.unit);
  const missingPdfs = rows.filter((row) => row.actions.needsPdf).map((row) => row.unit);
  const regenerable = rows
    .filter((row) => row.actions.canGeneratePdf && row.actions.showRegeneratePdf)
    .map((row) => row.unit);
  const pdfTargets = rows.filter((row) => row.actions.canGeneratePdf).map((row) => row.unit);

  return {
    draftsToCreate,
    draftCount: draftUnits.length,
    postedCount: postedUnits.length,
    readyToPostCount: readyToPost.length,
    missingPdfCount: missingPdfs.length,
    readyToPost,
    missingPdfs,
    regenerable,
    pdfTargets,
    allUnitsHaveInvoices: draftsToCreate === 0 && units.length > 0,
    canGenerateDrafts: draftsToCreate > 0,
    canPostAll: readyToPost.length > 0,
    canGenerateMissingPdfs: missingPdfs.length > 0,
    rows,
  };
}
